import React, { lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    currentVideoDetailsSelector,
    playlistSelector,
    selectedActSelector,
    selectedProjectComents,
    verticalPlayerSelector,
} from '../Dashboard/selectors';
import { userLikesSelector } from '../../selectors/selectors';
import { votingBoothItemsSelector } from '../../selectors/selectors';
import {
    deleteComment,
    deleteLike,
    getProjectDetails,
    leaveComment,
    likeComment,
    likeProject,
    setAutoPlay,
    setmedia,
    updateComment,
} from '../../redux/project/actions';
import { setMute } from '../../redux/player/action';
import { setPlayerQuery } from '../../redux/project/actions';
import { ILike } from '../../redux/account/types';
import { IActSummary } from '../../types/global/media.types';
import { IProject, Likes, TComment } from '../../types/global/helper';
import { ICreateView } from '../../redux/view/types';
import { createView } from '../../redux/view/action';
import { hideAdBanner, trackAd } from '../../redux/adverts/action';
import { IAdvertisement } from '../../types/global/media.types';
import { Loading } from '../../components/Loading/Loading';
import type { IVerticalVideoPlayer } from '../../components/VideoPlayer/VerticalVideoPlayer';

const VerticalVideoPlayer = lazy<React.FC<IVerticalVideoPlayer>>(() =>
    // @ts-ignore — dynamic import path (nodeNext); same pattern as AppLayout
    import('../../components/VideoPlayer/VerticalVideoPlayer').then(module => ({
        default: module.VerticalVideoPlayer,
    })),
);

/**
 * Video player overlay for brand dashboard routes (outside AppLayout).
 * Submissions "View Submission" dispatches setmedia — this mounts the player.
 */
export function BrandDashboardMediaOverlays() {
    const dispatch = useDispatch();
    const selectedAct = useSelector(selectedActSelector);
    const actPlayerOpen = useSelector(verticalPlayerSelector);
    const selectedProjectComments = useSelector(selectedProjectComents);
    const userLikes = useSelector(userLikesSelector);
    const currentPlaylist = useSelector(playlistSelector);
    const currentVideoDetails = useSelector(currentVideoDetailsSelector);
    const { showVoting2Fa, voteOtpSendingInProgress, startingIndex } = useSelector(votingBoothItemsSelector);

    const handleCloseMediaPlayer = () => {
        dispatch(setPlayerQuery({ isSequential: false, isSinglePreview: false }));
        dispatch(
            setmedia({
                media: null,
                mediaType: 'act',
                submissionObject: undefined,
                playlistType: 'recommendation',
                clearCustomSuggestion: true,
            }),
        );
        dispatch(setMute(true));
    };

    const handleSelectPlaylistItem = (video: IProject) => {
        if (video?.video_id) {
            dispatch(
                getProjectDetails({
                    id: video.video_id as string,
                    noPlaylistRefetch: true,
                    justFetchDetails: true,
                    projectToSet: video,
                    skipMediaSet: false,
                }),
            );
            dispatch(setMute(false));
            dispatch(setAutoPlay(true));
        }
    };

    const handleOnActLikePressed = () => {
        if (selectedAct) {
            dispatch(
                likeProject({
                    like_category: 'project',
                    like_type: 'like',
                    projectId: selectedAct._id as string,
                    like_origin: 'home',
                    location: 'home',
                }),
            );
        }
    };

    const handleOnDeleteLike = (like: ILike) => {
        dispatch(deleteLike({ documentId: like._id, payload: 'home', additionalPayload: like.project }));
    };

    const handleOnCommentSend = (
        text: string,
        comment?: TComment,
        isReply?: boolean,
        project?: IActSummary,
        isEdit?: boolean,
    ) => {
        if (isEdit && comment) {
            dispatch(updateComment({ documentId: comment._id, payload: text }));
        } else {
            dispatch(
                leaveComment({
                    comment: text,
                    project: project as any,
                    commentId: comment?._id,
                    commentType: 'project',
                    isReply,
                    parentComment: comment,
                    location: 'home',
                }),
            );
        }
    };

    const handleOnDeleteComment = (comment: TComment) => {
        dispatch(deleteComment({ documentId: comment._id, additionalPayload: comment._id, payload: comment }));
    };

    const handleCommentReaction = (like_type: Likes, comment: TComment) => {
        if (like_type === 'like') {
            dispatch(likeComment({ commentId: comment._id, like_category: 'comment', like_type, like_origin: 'home' }));
        } else {
            const likeToBeDeleted = userLikes.find(
                (like: ILike) =>
                    like.comment === comment._id && like.like_type === 'like' && like.like_category === 'comment',
            );
            if (likeToBeDeleted) {
                dispatch(deleteLike({ documentId: likeToBeDeleted._id, additionalPayload: likeToBeDeleted }));
            }
        }
    };

    const handleViewUpdate = (payload: ICreateView) => {
        dispatch(createView(payload));
    };

    const handleAdClick = (ad: IAdvertisement) => {
        if (ad?._id && selectedAct) {
            dispatch(
                trackAd({
                    additionalPayload: ad,
                    action: 'clickthrough',
                    id: ad._id,
                    project: selectedAct._id,
                }),
            );
            window.open(ad.click_through, '_blank', 'noopener,noreferrer');
        }
    };

    if (!selectedAct) {
        return null;
    }

    return (
        <Suspense fallback={<Loading disableBG />}>
            <VerticalVideoPlayer
                modalOpen={actPlayerOpen}
                onHandleVotingModalClose={() => {}}
                openVotngVerificationModal={showVoting2Fa}
                onClose={handleCloseMediaPlayer}
                project={selectedAct}
                comments={selectedProjectComments}
                userLikes={userLikes}
                onLikePressed={handleOnActLikePressed}
                onDeleteLike={handleOnDeleteLike}
                onCommentSend={handleOnCommentSend}
                onReactionPress={handleCommentReaction}
                onDeleteComment={handleOnDeleteComment}
                playlist={currentPlaylist}
                onReportClick={() => {}}
                onCoCreatorClick={() => {}}
                onDetailsClick={() => {}}
                onViewUpdate={handleViewUpdate}
                isVotingEnabled={false}
                isUserJuryEntitled={false}
                onVoteClicked={() => {}}
                hasVoted={false}
                onClickUserIcon={() => {}}
                feed={[]}
                onSwipeDown={() => {}}
                onSwipeUp={() => {}}
                loadMoreFeed={() => {}}
                voteOtpSendingInProgress={voteOtpSendingInProgress}
                startingIndex={startingIndex}
                currentVideoDetails={currentVideoDetails}
                onClickPlaylistItem={handleSelectPlaylistItem}
                onAdClick={handleAdClick}
                closeAd={() => dispatch(hideAdBanner())}
            />
        </Suspense>
    );
}
